import { ChangeDetectorRef, Component, OnInit, ViewChild, inject } from '@angular/core';
import { WorkflowForm, WorkflowFormComponent, WorkflowFormKey, WorkflowTaskDataService } from '@lib/workflow-integration';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import {
    IPurchaseRequisitionTaskPreview,
    PurchaseRequisitionTaskPreview,
} from '../../types/PurchaseRequisitionTaskPreview';
import { DemandDataService } from '../../../demand/demand-data.service';
import { CommentDataService } from '@lib/comment';
import { ItemClassificationDataService } from '@lib/classification';
import { FileDataService } from '@lib/file';
import {
    createDeeplinkCompletionContext,
    CustomRouter,
    DeeplinkCompletionAction,
    DeeplinkCompletionContextService,
} from '@lib/core';
import { ActivatedRoute } from '@angular/router';
import { PurchaseRequisitionDetailComponent } from '../../../purchase-requisition/purchase-requisition-detail/purchase-requisition-detail.component';
import {
    IPurchaseRequisitionChooseSingleApproverTask,
    PurchaseRequisitionChooseSingleApproverTask,
} from './types/PurchaseRequisitionChooseSingleApproverTask';
import { FormControl } from '@angular/forms';
import { PersonExtended } from '@lib/user';
import { PurchaseRequisitionApproveDeclineReworkCompletionData } from './types/PurchaseRequisitionChooseSingleApproverCompletionData';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
    IPurchaseRequisitionChooseSingleApproverDialogResult,
    PurchaseRequisitionChooseSingleApproverDialogComponent,
    PurchaseRequisitionChooseSingleApproverDialogData,
} from './dialog/purchase-requisition-choose-single-approver-dialog/purchase-requisition-choose-single-approver-dialog.component';
import { DialogOptions, Logger } from '@lib/util';
import { takeUntil } from 'rxjs/operators';
import { I18nService } from '@lib/i18n';
import { HttpErrorResponse } from '@angular/common/http';
import { DemandValidationError, IDemandValidationError } from '../../../demand/validation/types/IDemandValidationError';
import { ValidationSubject } from '../../../demand/validation/types/IValidationError';

@WorkflowForm(
    marker('Freigeber wählen'),
    [WorkflowFormKey.PURCHASE_REQUISITION_CHOOSE_SINGLE_APPROVER_FORM],
    (dto: IPurchaseRequisitionTaskPreview, displayName: string) => new PurchaseRequisitionTaskPreview(dto, displayName),
    marker('Freigeber wählen'),
)
@Component({
    selector: 'purchase-requisition-choose-single-approver-task',
    templateUrl: './purchase-requisition-choose-single-approver-task.component.html',
    styleUrls: ['./purchase-requisition-choose-single-approver-task.component.scss'],
    standalone: false,
})
export class PurchaseRequisitionChooseSingleApproverTaskComponent extends WorkflowFormComponent implements OnInit {
    private readonly taskDataService = inject(WorkflowTaskDataService);
    private readonly demandDataService = inject(DemandDataService);
    private readonly commentDataService = inject(CommentDataService);
    private readonly itemClassificationDataService = inject(ItemClassificationDataService);
    private readonly fileDataService = inject(FileDataService);
    private readonly router = inject(CustomRouter);
    private readonly route = inject(ActivatedRoute);
    private readonly dialog = inject(MatDialog);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly i18n = inject(I18nService);
    private readonly deeplinkCompletionContext = inject(DeeplinkCompletionContextService);

    @ViewChild('purchaseRequisitionDetailComponent')
    public readonly purchaseRequisitionDetailComponent?: PurchaseRequisitionDetailComponent;
    public task?: PurchaseRequisitionChooseSingleApproverTask;

    public selectedApproverCtrl?: FormControl<PersonExtended | undefined | null>;

    public get approverLabel(): string {
        return this.task?.approverRequired ? marker('Freigeber wählen') : marker('Optionalen Freigeber wählen');
    }

    /**
     * EPROC-11560: mirrors exactly the guard of {@link complete}. An invalid purchase requisition — e.g.
     * a document currency the supplier does not accept — blocks the continue button on its own, not only
     * in combination with a missing mandatory approver. Otherwise the button stayed clickable although
     * the click could never do anything.
     */
    public get completionBlocked(): boolean {
        return (
            !this.purchaseRequisitionDetailComponent?.isValid ||
            (!this.selectedApproverCtrl?.value && !!this.task?.approverRequired) ||
            this.isCompleted
        );
    }

    ngOnInit(): void {
        this.taskId = this.taskId ? this.taskId : this.route.snapshot.params['taskId'];
        if (this.taskId) {
            this.getTask();
        }
    }

    public async complete() {
        if (
            !this.isCompleted &&
            this.task &&
            this.purchaseRequisitionDetailComponent?.isValid &&
            (this.selectedApproverCtrl?.value || !this.task.approverRequired)
        ) {
            this._isCompleted = true;
            const selectedApprover = this.selectedApproverCtrl?.value ? this.selectedApproverCtrl.value?.id : undefined;
            await this.task.purchaseRequisition.demand.saveMetaData();
            this.taskDataService
                .completeTask(this.task.id, new PurchaseRequisitionApproveDeclineReworkCompletionData(selectedApprover))
                .subscribe({
                    next: () => {
                        this.deeplinkCompletionContext.completeRun(
                            createDeeplinkCompletionContext(
                                selectedApprover
                                    ? DeeplinkCompletionAction.PURCHASE_REQUISITION_FORWARDED
                                    : DeeplinkCompletionAction.PURCHASE_REQUISITION_APPROVED,
                                this.task!.purchaseRequisition.number,
                            ),
                        );
                        this.completed.emit();
                        if (this.routeToListAfterCompletion) {
                            this.router.navigate(
                                [this.router.getViewFromRoute(), 'rfq', 'purchase-requisition', 'list'],
                                {
                                    replaceUrl: true,
                                    queryParams: {
                                        clean: true,
                                    },
                                },
                            );
                        }
                    },
                    error: (error: HttpErrorResponse) => {
                        this._isCompleted = false;

                        const validationError: IDemandValidationError | undefined = error?.error?.error;
                        if (
                            validationError &&
                            validationError.subject === ValidationSubject.DEMAND &&
                            this.task?.purchaseRequisition
                        ) {
                            this.task.purchaseRequisition.demand.setValidationError(
                                new DemandValidationError(validationError, this.i18n),
                            );
                        }
                    },
                });
        }
    }

    public getTask() {
        this.taskDataService
            .getSingleTaskById<IPurchaseRequisitionChooseSingleApproverTask>(this.taskId)
            .subscribe(async (task: IPurchaseRequisitionChooseSingleApproverTask) => {
                this.task = new PurchaseRequisitionChooseSingleApproverTask(
                    task,
                    this.demandDataService,
                    this.commentDataService,
                    this.itemClassificationDataService,
                    this.fileDataService,
                    this.i18n,
                );
                this.selectedApproverCtrl = new FormControl<PersonExtended | undefined>(this.task.defaultApprover);
                // detect that the component is now available
                this.cdr.detectChanges();
                await this.purchaseRequisitionDetailComponent?.initialized
                    .pipe(takeUntil(this.onDestroy$))
                    .subscribe(() => {
                        this.initialized.emit();
                    });
            });
    }

    override async postCreation(): Promise<void> {
        if (this.task) {
            const data = DialogOptions.AUTO(
                new PurchaseRequisitionChooseSingleApproverDialogData(
                    this.task.possibleApprovers,
                    this.task.defaultApprover,
                    this.task.approverRequired,
                ),
            );
            data.disableClose = true;

            const ref: MatDialogRef<
                PurchaseRequisitionChooseSingleApproverDialogComponent,
                IPurchaseRequisitionChooseSingleApproverDialogResult | undefined
            > = this.dialog.open(PurchaseRequisitionChooseSingleApproverDialogComponent, data);
            ref.afterClosed().subscribe((result?: IPurchaseRequisitionChooseSingleApproverDialogResult) => {
                if (result && !result.cancelled) {
                    if (result.selectedApprover && this.selectedApproverCtrl) {
                        this.selectedApproverCtrl.setValue(result.selectedApprover);
                    }
                    this.complete();
                }
            });
        } else {
            Logger.error('POST_CREATION_ACTION', 'Action defined but the task is not defined');
        }
    }
}
